import type {Editor, ProjectData} from 'grapesjs';
import type {ColorPaletteInfoToStore} from '@Libraries/auto-branding-email-maker';
import type {SymbolStorageData} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, NEW_TEMPLATE_NAME} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import {getNumberProp, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {assertNever} from '@Utils/assert-never.util';

const PROJECT_DATA_PAGES_KEY = 'pages';
const PROJECT_DATA_SYMBOLS_KEY = 'symbols';

export interface EmailMakerSavePayloadSources {
  /** The juice-inlined export HTML for the whole email. */
  getInlinedHtml: (editor: Editor) => string;
  extractColorPaletteDataToStore?: (editor: Editor) => ColorPaletteInfoToStore | null;
}

/** Template name lives on the PMW wrapper component; empty/missing falls back to the default. */
export function getTemplateName(editor: Editor): string {
  const wrapperComponent = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER)[0];
  const name = wrapperComponent ? getStringProp(wrapperComponent, EMAIL_MAKER_PROPS.TEMPLATE_NAME) : undefined;
  // An empty saved name must also fall back to the default.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return name || NEW_TEMPLATE_NAME;
}

/** Returns comma-separated, de-duplicated hashed design ids of all PMW designs in the email. */
export function getDesignIds(editor: Editor): string {
  const ids = new Set<string>();
  editor.getWrapper()?.onAll((component) => {
    if (component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN)) {
      const id = getStringProp(component, EMAIL_MAKER_PROPS.DESIGN_ID);
      if (id) {
        ids.add(id);
      }
    }
  });
  return Array.from(ids).join(',');
}

/** All CSS rules serialized one-per-line, CRLF-separated — the persisted media-query wire format. */
export function getMediaQueries(editor: Editor): string {
  return editor.Css.getRules()
    .map((rule) => {
      return rule.toCSS();
    })
    .join('\r\n');
}

/** Returns comma-separated hashed filenames for all PMW image components. */
export function getImageAssetHashedNames(editor: Editor): string {
  return getHashedFileNamesFromType(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE);
}

/** Returns comma-separated hashed filenames for all PMW video components. */
export function getVideoAssetHashedNames(editor: Editor): string {
  return getHashedFileNamesFromType(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO);
}

/** Returns true if any image, video, sticker, or design component in the email is a GIF. */
export function doesEmailContainGif(editor: Editor): boolean {
  return (
    doesComponentTypeHaveGif(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO) ||
    doesComponentTypeHaveGif(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE) ||
    doesComponentTypeHaveGif(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER) ||
    doesComponentTypeHaveGif(editor, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN)
  );
}

/** Returns true if the email contains any Getty images or videos. */
export function doesEmailContainPremiumContent(editor: Editor): boolean {
  return getGettyMediaCount(editor, EmailMakerMediaType.IMAGE) > 0 || getGettyMediaCount(editor, EmailMakerMediaType.VIDEO) > 0;
}

/** The Getty count is cached as a prop on the main content component. */
export function getGettyMediaCount(editor: Editor, mediaType: EmailMakerMediaType): number {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper || !editor.getComponents().models.length) {
    return 0;
  }
  const property = getGettyCountPropForMediaType(mediaType);
  if (property === null) {
    return 0;
  }
  const mainContent = wrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT}"]`)[0];
  return (mainContent ? getNumberProp(mainContent, property) : undefined) ?? 0;
}

/** Each top-level project-data key is JSON-stringified individually — the backend stores templateContent as a map of these JSON strings. */
export function buildTemplateContentForSave(projectData: ProjectData): Record<string, string> {
  const templateContent: Record<string, string> = {};
  Object.keys(projectData).forEach((key: string) => {
    templateContent[key] = JSON.stringify(getStorableProjectDataValue(key, projectData[key]));
  });
  return templateContent;
}

function getStorableProjectDataValue(key: string, value: unknown): unknown {
  if (key === PROJECT_DATA_PAGES_KEY) {
    return getPagesDataWithoutReadOnlyFooter(value);
  }
  if (key === PROJECT_DATA_SYMBOLS_KEY) {
    // Symbol data is never persisted; replaced with this static placeholder.
    const symbolStaticData: SymbolStorageData = {content: '[]', type: 'textnode'};
    return [symbolStaticData];
  }
  return value;
}

interface StoredComponentData {
  type?: string;
}

interface StoredFrameData {
  component?: {
    components?: StoredComponentData[];
  };
}

interface StoredPageData {
  frames?: StoredFrameData[];
}

/** The read-only footer is display-only; pops it off the serialized first frame when it is the last component. */
function getPagesDataWithoutReadOnlyFooter(pagesData: unknown): unknown {
  const pages = pagesData as StoredPageData[] | undefined;
  const components = pages?.[0]?.frames?.[0]?.component?.components;
  if (components && components.length > 0 && components[components.length - 1].type === EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_READ_ONLY_FOOTER) {
    components.pop();
  }
  return pagesData;
}

function getHashedFileNamesFromType(editor: Editor, type: string): string {
  const fileNames = new Set<string>();
  editor.getWrapper()?.onAll((component) => {
    if (component.is(type)) {
      const fileName = getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME);
      if (fileName) {
        fileNames.add(fileName);
      }
    }
  });
  return Array.from(fileNames).join(',');
}

function doesComponentTypeHaveGif(editor: Editor, componentType: string): boolean {
  const allComponents = editor.getWrapper()?.findType(componentType);
  if (!allComponents) {
    return false;
  }
  return allComponents.some((component) => {
    // An empty-string src prop must fall through to the src attribute.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const src: string = (component.get('src') as string | undefined) || (component.getAttributes().src as string | undefined) || '';
    const srcWithoutArgs: string = src.split('?')[0];
    return /\.gif$/i.test(srcWithoutArgs);
  });
}

/** Only image/video map to count props; every other media type returns null (count 0). */
export function getGettyCountPropForMediaType(mediaType: EmailMakerMediaType): string | null {
  switch (mediaType) {
    case EmailMakerMediaType.IMAGE:
      return EMAIL_MAKER_PROPS.PREMIUM_IMAGE_COUNT;
    case EmailMakerMediaType.VIDEO:
      return EMAIL_MAKER_PROPS.PREMIUM_VIDEO_COUNT;
    case EmailMakerMediaType.ANIMATED_STICKER:
    case EmailMakerMediaType.STILL_STICKER:
    case EmailMakerMediaType.ICON:
      return null;
    default:
      return assertNever(mediaType);
  }
}
